/**
 * Parses a Capacitor `appUrlOpen` deep-link URL for the OAuth callback.
 *
 * Native OAuth redirects to `com.malkkov.volyx://auth/callback?code=...`. This
 * extracts the PKCE code and the params `AuthCallbackPage` still needs, and
 * returns `null` for any URL that is not the auth callback so unrelated deep
 * links are ignored.
 */

export interface OAuthCallbackDeepLink {
  /** PKCE authorization code to exchange for a session, if present. */
  code: string | null;
  /** Provider error description, if the redirect carried one. */
  errorDescription: string | null;
  /**
   * Query string (leading `?` included, or empty) preserving only the params
   * `AuthCallbackPage` reads: `signup`, `next`, `error`, `error_description`.
   */
  forwardedSearch: string;
}

const FORWARDED_PARAMS = ['signup', 'next', 'error', 'error_description'] as const;

export function parseOAuthCallbackDeepLink(url: string): OAuthCallbackDeepLink | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  // For `com.malkkov.volyx://auth/callback`, host is `auth` and pathname is
  // `/callback`. Match on the combined `auth/callback` to stay scheme-agnostic.
  const combined = `${parsed.host}${parsed.pathname}`.replace(/\/+$/, '');
  if (!combined.endsWith('auth/callback')) {
    return null;
  }

  const params = parsed.searchParams;

  const forwarded = new URLSearchParams();
  for (const key of FORWARDED_PARAMS) {
    const value = params.get(key);
    if (value !== null) {
      forwarded.set(key, value);
    }
  }
  const forwardedString = forwarded.toString();

  return {
    code: params.get('code'),
    errorDescription: params.get('error_description'),
    forwardedSearch: forwardedString ? `?${forwardedString}` : '',
  };
}
